import React from 'react';
import { percentageToNdecialPlacesNoHundred } from '../../../../utilities/numberFormatters';

const THRESHOLD_DECIMALS = 2;

export type NavReconThresholdTone = 'alert' | 'fail' | 'upper' | 'lower';

export type NavReconThresholdItem = {
  key: string;
  label: string;
  value: number;
  tone: NavReconThresholdTone;
};

/** A linked alert/fail pair (and future threshold sets grouped separately). */
export type NavReconThresholdGroup = {
  id: string;
  /** Optional label when multiple threshold types are shown, e.g. "P/L gap". */
  label?: string;
  items: NavReconThresholdItem[];
};

interface NavReconThresholdsProps {
  groups: NavReconThresholdGroup[];
}

const isValidThreshold = (value: number | undefined): value is number =>
  value != null && !Number.isNaN(value);

const toneStyles: Record<
  NavReconThresholdTone,
  { dot: string; label: string; value: string }
> = {
  alert: {
    dot: 'bg-alert',
    label: 'text-alert_dark',
    value: 'text-alert_dark',
  },
  fail: {
    dot: 'bg-fail',
    label: 'text-fail',
    value: 'text-fail',
  },
  lower: {
    dot: 'bg-slate-500',
    label: 'text-slate-700',
    value: 'text-slate-800',
  },
  upper: {
    dot: 'bg-neutral-500',
    label: 'text-neutral-700',
    value: 'text-neutral-800',
  },
};

const NavReconThresholdGroupRow: React.FC<{ group: NavReconThresholdGroup }> = ({
  group,
}) => {
  const items = group.items.filter((item) => isValidThreshold(item.value));
  if (!items.length) return null;

  return (
    <div
      className="flex items-center overflow-hidden rounded-md border border-gray-200 bg-gray-50/80"
      aria-label={group.label ?? 'Threshold group'}
    >
      {group.label ? (
        <div className="flex items-center border-r border-gray-200 bg-white/70 px-2.5 py-1">
          <span className="whitespace-nowrap text-xs font-medium leading-none text-gray-600">
            {group.label}
          </span>
        </div>
      ) : null}
      {items.map((item, index) => {
        const styles = toneStyles[item.tone];
        return (
          <div
            key={item.key}
            className={`flex items-center gap-1.5 px-2.5 py-1 leading-none ${index > 0 ? 'border-l border-gray-200' : ''
              }`}
          >
            <span className={`h-2 w-2 shrink-0 rounded-full ${styles.dot}`} />
            <span className={`font-medium ${styles.label}`}>{item.label}:</span>
            <span className={`font-bold ${styles.value}`}>
              {percentageToNdecialPlacesNoHundred(item.value, THRESHOLD_DECIMALS)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export const buildNavReconGapThresholdGroup = (
  alertThreshold?: number,
  failThreshold?: number,
  options?: { id?: string; label?: string },
): NavReconThresholdGroup | null => {
  const items: NavReconThresholdItem[] = [];

  if (isValidThreshold(alertThreshold)) {
    items.push({
      key: 'alert',
      label: 'Alert',
      value: alertThreshold,
      tone: 'alert',
    });
  }

  if (isValidThreshold(failThreshold)) {
    items.push({
      key: 'fail',
      label: 'Fail',
      value: failThreshold,
      tone: 'fail',
    });
  }

  if (!items.length) return null;

  return {
    id: options?.id ?? 'gap',
    label: options?.label,
    items,
  };
};

export const buildNavReconPositionSizeChangeThresholdGroup = (
  threshold?: number,
  options?: { id?: string; label?: string },
): NavReconThresholdGroup | null => {
  if (!isValidThreshold(threshold)) return null;

  return {
    id: options?.id ?? 'position-size-change',
    label: options?.label,
    items: [
      {
        key: 'threshold',
        label: 'Threshold',
        value: threshold,
        tone: 'alert',
      },
    ],
  };
};

export const buildNavReconPriceVariationThresholdGroup = (
  lowerThreshold?: number,
  upperThreshold?: number,
  options?: { id?: string; label?: string },
): NavReconThresholdGroup | null => {
  const items: NavReconThresholdItem[] = [];

  if (isValidThreshold(lowerThreshold)) {
    items.push({
      key: 'lower',
      label: 'Lower',
      value: lowerThreshold,
      tone: 'lower',
    });
  }

  if (isValidThreshold(upperThreshold)) {
    items.push({
      key: 'upper',
      label: 'Upper',
      value: upperThreshold,
      tone: 'upper',
    });
  }

  if (!items.length) return null;

  return {
    id: options?.id ?? 'price-variation',
    label: options?.label,
    items,
  };
};

const NavReconThresholds: React.FC<NavReconThresholdsProps> = ({ groups }) => {
  const visibleGroups = groups
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => isValidThreshold(item.value)),
    }))
    .filter((group) => group.items.length > 0);

  if (!visibleGroups.length) return null;

  return (
    <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white/60 px-3 py-1 text-sm leading-none shadow-sm">
      <span className="whitespace-nowrap border-r border-gray-200 pr-3 font-semibold leading-none text-mainblue">
        Thresholds
      </span>
      <div className="flex flex-wrap items-center gap-2">
        {visibleGroups.map((group) => (
          <NavReconThresholdGroupRow key={group.id} group={group} />
        ))}
      </div>
    </div>
  );
};

export default NavReconThresholds;
